import {
  CheckIcon,
  ExclamationIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import { useEffect, useState, type FC, type ReactNode } from 'react';
import { useHover } from 'react-aria';
import type { AlertIdsEnum } from 'types/AlertIdsEnum';
import type { color } from 'types/shared/worlds';
import {
  isAlertDismissed,
  markAlertAsDismissed,
} from 'utils/AlertsStoreManager';
import { getTracingEvents } from 'utils/amplitude';
import { WEBAPP_GLOBAL_WARNINGBOX } from 'utils/amplitude/events/global.amplitude';
import { XIconOutline } from 'utils/icons';
import { Button } from './Button';
import { Loader } from './Loader';

const events = getTracingEvents({
  onWarningMount: {
    name: WEBAPP_GLOBAL_WARNINGBOX,
    data: (name: string) => ({ name }),
  },
});

const themesClasses = {
  info: {
    wrapper: 'bg-taxes-50 border border-taxes-200',
    title: 'text-primary',
    icon: '!w-6 !h-6 text-primary-700',
    subtitle: 'text-primary-700',
  },
  warning: {
    wrapper: 'bg-yellow-50 border border-yellow-200',
    title: 'text-yellow-800',
    icon: '!w-6 !h-6 text-yellow-800',
    subtitle: 'text-yellow-800',
  },
  error: {
    wrapper: 'bg-red-50 border border-red-200',
    title: 'text-red-800',
    icon: '!w-6 !h-6 text-red-400',
    subtitle: 'text-red-800',
  },
  success: {
    wrapper: 'bg-green-50 border border-green-200',
    title: 'text-green-800',
    icon: '!w-6 !h-6 text-green-700',
    subtitle: 'text-green-800',
  },
  bank: {
    wrapper: 'bg-bank-50 border border-bank-200',
    title: 'text-blue-900',
    icon: '!w-6 !h-6 text-blue-900',
    subtitle: 'text-blue-900',
  },
  expenses: {
    wrapper: 'bg-expenses-50 border border-expenses-200',
    title: 'text-danger-dark',
    icon: '!w-6 !h-6 text-danger-dark',
    subtitle: 'text-danger-dark',
  },
  revenues: {
    wrapper: 'bg-revenues-50 border border-revenues-200',
    title: 'text-success-dark',
    icon: '!w-6 !h-6 text-success-dark',
    subtitle: 'text-success-dark',
  },
  taxes: {
    wrapper: 'bg-taxes-50 border border-taxes-200',
    title: 'text-taxes-950',
    icon: '!w-6 !h-6 text-taxes-950',
    subtitle: 'text-taxes-950',
  },
};

const getIconForType = (
  type: 'info' | 'warning' | 'error' | 'success' | Exclude<color, 'primary'>,
) => {
  if (type === 'warning' || type === 'error') return ExclamationIcon;
  if (type === 'success') return CheckIcon;
  return InformationCircleIcon;
};

const SimpleAlertV2: FC<{
  type: 'info' | 'warning' | 'error' | 'success' | Exclude<color, 'primary'>;
  title?: string;
  subtitle?: string;
  extra?: ReactNode;
  CustomIcon?: FC;
  wrapperClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  onDismiss?: () => void;
  dismissId?: AlertIdsEnum;
  dismissable?: boolean;
  name?: string; // for amplitude
  size?: 'sm' | 'normal';
  extraContent?: ReactNode;
  verticalAlignment?: 'middle' | 'top';
  Graphics?: FC<{ isHovered: boolean }>;
  onBannerClick?: () => void;
  loading?: boolean;
}> = ({
  type,
  title,
  extra,
  CustomIcon,
  subtitle,
  titleClassName,
  subtitleClassName,
  dismissable,
  onDismiss,
  dismissId,
  name,
  size,
  wrapperClassName,
  extraContent,
  verticalAlignment = 'middle',
  Graphics,
  onBannerClick,
  loading,
}) => {
  const [isHovered, onHoverChange] = useState(false);

  const { hoverProps } = useHover({ onHoverChange });

  const Icon = CustomIcon || getIconForType(type);

  const fontSize = size === 'sm' ? 'text-sm' : 'text-base';

  const [dismissed, setDismissed] = useState(() => {
    if (dismissId) {
      return isAlertDismissed(dismissId);
    }
    return false;
  });

  useEffect(() => {
    if (name) events.onWarningMount(name);
  }, [name]);

  if (dismissed) return null;

  const dismiss = () => {
    if (dismissId) {
      markAlertAsDismissed(dismissId);
    }
    setDismissed(true);
    onDismiss?.();
  };

  const theme = themesClasses[type];

  return (
    <div
      {...hoverProps}
      className={classNames(
        'w-full flex justify-between p-4 rounded-xl overflow-hidden',
        theme.wrapper,
        verticalAlignment === 'middle' ? 'items-center' : 'items-start',
        wrapperClassName,
        { 'cursor-pointer': onBannerClick },
      )}
      onClick={onBannerClick}
    >
      <div
        className={`flex space-x-4 ${
          verticalAlignment === 'middle' ? 'items-center' : 'items-start'
        }`}
      >
        {loading ? (
          <Loader
            className={classNames(
              theme.icon,
              verticalAlignment === 'top' && 'mt-2',
            )}
          />
        ) : (
          <Icon
            className={classNames(
              theme.icon,
              verticalAlignment === 'top' && 'mt-2',
            )}
          />
        )}

        <div>
          {title ? (
            <p
              className={classNames(
                'font-semibold text-base',
                theme.title,
                fontSize,
                titleClassName,
              )}
            >
              {title}
            </p>
          ) : null}

          {subtitle ? (
            <p
              className={classNames(
                'text-primary-700 text-sm',
                theme.subtitle,
                fontSize,
                subtitleClassName,
              )}
            >
              {subtitle}
            </p>
          ) : null}

          {extraContent}
        </div>
      </div>

      {Graphics ? <Graphics isHovered={isHovered} /> : null}

      <div className="flex items-center">
        {extra ? <div className="mr-4">{extra}</div> : null}

        {dismissable ? (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            structure="text"
          >
            <XIconOutline className={classNames(theme.icon, 'opacity-40')} />
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default SimpleAlertV2;
